import { useMemo } from "react";
import { DatePickerField } from "@/components/ui/date-picker-field";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface BidDeadlinePickerProps {
  /** Local datetime string `YYYY-MM-DDTHH:mm` or empty. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/** Sentinel time = 23:59 means "end of day / no explicit time set". */
const EOD = "23:59";

/** 48 options: 00:00, 00:30, ..., 23:30 */
const TIME_OPTIONS: { value: string; label: string }[] = (() => {
  const opts: { value: string; label: string }[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      const period = h < 12 ? "AM" : "PM";
      const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
      opts.push({
        value: `${hh}:${mm}`,
        label: `${display}:${mm} ${period}`,
      });
    }
  }
  return opts;
})();

function splitValue(value: string): { date: string | null; time: string } {
  if (!value) return { date: null, time: EOD };
  const [d, t] = value.split("T");
  return { date: d || null, time: (t || EOD).slice(0, 5) };
}

function formatPreview(value: string, isDefaultTime: boolean): string | null {
  if (!value) return null;
  const dt = new Date(value);
  if (isNaN(dt.getTime())) return null;
  const datePart = dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const timePart = dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  let tz = "";
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).formatToParts(dt);
    tz = parts.find(p => p.type === "timeZoneName")?.value || "";
  } catch { /* noop */ }
  const suffix = isDefaultTime ? " (defaults to end of day)" : "";
  return `Bids close ${datePart} at ${timePart}${tz ? ` ${tz}` : ""}${suffix}`;
}

export default function BidDeadlinePicker({ value, onChange, disabled }: BidDeadlinePickerProps) {
  const { date, time } = useMemo(() => splitValue(value), [value]);
  const isDefaultTime = time === EOD;

  const setDate = (newDate: string | null) => {
    if (!newDate) {
      onChange("");
      return;
    }
    onChange(`${newDate}T${time || EOD}`);
  };

  const setTime = (newTime: string) => {
    // Empty string = "no time" → default to end of day
    const effective = newTime || EOD;
    if (!date) {
      const today = new Date();
      const y = today.getFullYear();
      const m = String(today.getMonth() + 1).padStart(2, "0");
      const d = String(today.getDate()).padStart(2, "0");
      onChange(`${y}-${m}-${d}T${effective}`);
      return;
    }
    onChange(`${date}T${effective}`);
  };

  const preview = formatPreview(value, isDefaultTime);

  return (
    <div className="space-y-1.5">
      <div className="grid grid-cols-2 gap-2">
        <DatePickerField
          value={date}
          onChange={setDate}
          disabled={disabled}
          placeholder="Pick date"
          displayFormat="MMM d, yyyy"
          className="h-8 text-xs"
        />
        <Select
          value={isDefaultTime ? "__eod__" : time}
          onValueChange={(v) => setTime(v === "__eod__" ? "" : v)}
          disabled={disabled}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="End of day (11:59 PM)" />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="__eod__" className="text-xs">
              End of day (11:59 PM)
            </SelectItem>
            {TIME_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value} className="text-xs">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <p className="text-[10px] text-muted-foreground">
        Time is optional — defaults to 11:59 PM on the selected date.
      </p>
      {preview && (
        <p className="text-[10px] text-muted-foreground">{preview}</p>
      )}
    </div>
  );
}
